import React, { ChangeEvent, useMemo, useState } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { ReactComponent as ErrorIcon } from '../Icons/shared/ic-shared-error.svg';
import { ReactComponent as ClearIcon } from '../Icons/shared/ic-shared-clear.svg';

interface LiliTextAreaProps {
  isDisabled?: boolean;
  maxLength?: number;
  minLength?: number;
  placeholder?: string;
  value: string;
  onValueChange?: (value: string) => void;
  errorMsg?: string;
  onBlur?: () => void;
  onClear?: () => void;
}

export const LiliTextArea = ({
  isDisabled,
  maxLength,
  minLength,
  placeholder,
  value,
  onValueChange,
  errorMsg,
  onBlur,
  onClear,
}: LiliTextAreaProps) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (onValueChange) onValueChange(e.target.value);
  };

  const handleClear = () => {
    if (onValueChange) onValueChange('');
    if (onClear) onClear();
  };

  const isInputInvalid = useMemo(() => {
    if (!maxLength) return false;
    return value.length > maxLength;
  }, [value, maxLength]);

  return (
    <div className='flex flex-col relative gap-[8px] rounded-[8px] w-full'>
      {value && (
        <button onClick={handleClear} type='button' className='absolute right-[8px] top-[8px]'>
          <ClearIcon className='w-[18px]' />
        </button>
      )}
      <InputTextarea
        value={value}
        onBlur={() => {
          if (onBlur) onBlur();
          setIsFocused(false);
        }}
        onFocus={() => setIsFocused(true)}
        placeholder={placeholder}
        onChange={handleChange}
        className={`shadow-none bg-bw00 hover:bg-bw01 resize-none placeholder:text-bw07 pl-[12px] pr-[40px] py-[8px] rounded-[8px] border ${isInputInvalid ? 'border-red02' : isFocused ? 'border-action03' : 'border-bw04'} font-metricR text-[14px] text-bw08 `}
        disabled={isDisabled}
        autoResize
      />
      {maxLength && (
        <div className='flex items-center justify-between'>
          <div className={`flex items-center gap-[2px] ${value.length > maxLength ? 'opacity-100' : 'opacity-0'}`}>
            <ErrorIcon />
            <p className='font-metricR text-[12px] text-red02'>{errorMsg || `Maximum ${maxLength} characters.`}</p>
          </div>
          <div className='flex items-center'>
            <p className={`font-metricR text-[12px] ${value.length > maxLength ? 'text-red02' : 'text-bw07'}`}>
              {value.length.toString()}
            </p>
            <p className='font-metricR text-[12px] text-bw07'>{`/${maxLength.toString()}`}</p>
          </div>
        </div>
      )}
      {minLength && value.length < minLength && (
        <p className='font-metricR absolute left-0 bottom-0 text-[12px] text-bw07'>{`Minimum ${minLength} characters.`}</p>
      )}
    </div>
  );
};
